import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, setToken, errMsg } from "@/lib/api";
import { applyBrandColor, rememberLastTenantSlug, getLastTenantSlug } from "@/lib/theme";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogIn } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState({ business_name: "", cover_url: "", brand_color: "", logo_url: "" });

  useEffect(() => {
    api.get("/auth/setup-status").then(({ data }) => {
      if (data.needs_setup) nav("/setup", { replace: true });
    }).catch(() => {});
    // Pick up the tenant branding from ?slug=xxx or the last-used slug in this
    // browser so the login screen shows the franquia's cover image and colour.
    const slug = sp.get("slug") || getLastTenantSlug() || "master";
    axios.get(`${API}/api/public/${slug}/settings`).then(({ data }) => {
      setBrand(data || {});
      applyBrandColor(data?.brand_color || "");
      if (data?.business_name) document.title = `${data.business_name} — Entrar`;
    }).catch(() => {});
  }, [nav, sp]);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Informe um email válido");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: trimmed, password });
      setToken(data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      rememberLastTenantSlug(data.user?.tenant_slug);
      toast.success(`Bem-vindo(a), ${data.user.name}`);
      nav("/", { replace: true });
    } catch (err) {
      toast.error(errMsg(err, "Falha no login"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden" data-testid="login-hero">
        {brand.cover_url ? (
          <img
            src={brand.cover_url}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/80 to-primary/60" />
        )}
        <div className="absolute inset-0 bg-primary/40 mix-blend-multiply" />
        <div className="relative h-full flex flex-col justify-end p-12 text-primary-foreground">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.business_name || "Logo"} className="h-14 w-auto object-contain mb-3 bg-white/10 backdrop-blur px-3 py-2 rounded-lg self-start" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="text-3xl font-display font-bold tracking-tight">{brand.business_name || "Agenda"}<span className="text-accent">.</span></div>
          )}
          <p className="mt-3 text-sm max-w-sm text-white/85">Gerencie serviços, profissionais, comissões e cobranças em um só lugar.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} noValidate className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Entrar</h1>
            <p className="text-sm text-muted-foreground mt-2">Acesse o painel do seu estabelecimento.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="input-email" type="text" required autoComplete="email" className="h-12" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Senha</Label>
            <Input id="password" data-testid="input-password" type="password" required autoComplete="current-password" className="h-12" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <Button data-testid="btn-submit-login" type="submit" disabled={loading} className="w-full h-12">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogIn className="h-4 w-4 mr-2" />}
            Entrar
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            Primeiro acesso? <a href="/setup" data-testid="link-setup" className="underline hover:text-foreground">Criar conta admin</a>
          </p>
        </form>
      </div>
    </div>
  );
}
